// -----------------------------------------------------------------------------
// 🧩 File: Membership.jsx
// -----------------------------------------------------------------------------
// ✅ Auto fleet popup for users with no request history (once)
// ✅ If approved: show Checkout + Request New
// ✅ Fleet checkout uses approved price + approved vehicles
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Classes from "./Membership.module.css";
import { EmailSentIcon, ExclamationIcon } from "../../../../../../../utils/Icons";
import NotifyPopup from "../../../../../../GlobalComponents/NotifyPopup/NotifyPopup";
import FleetMembPopup from "../../../../../../GlobalComponents/FleetMembPopup/FleetMembPopup";
import { useNavigate, useLocation } from "react-router-dom";
import backend from "../../../../../../../api";
import { useSelector } from "react-redux";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const cardIn = {
  hidden: { opacity: 0, y: 14, scale: 0.985 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35 } },
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const labelFor = (key) => {
  if (!key || key === "free") return "None";
  if (key === "tier1") return "Tier 1";
  if (key === "tier2") return "Tier 2";
  if (key === "tier3") return "Tier 3";
  if (key === "fleet") return "Fleet";
  return key?.charAt(0).toUpperCase() + key?.slice(1);
};

const cycleLabelFor = (cycle) => {
  if (!cycle) return "";
  if (cycle === "monthly") return "Monthly";
  if (cycle === "biannually") return "Bi-Annually";
  if (cycle === "annually") return "Annually";
  if (cycle === "yearly") return "Annually";
  return cycle;
};

export default function Membership() {
  const navigateTo = useNavigate();
  const location = useLocation();

  const [billing, setBilling] = useState("monthly"); // monthly | biannually | annually | fleet_request

  const { user, token } = useSelector((s) => s?.auth || {});
  const userId = user?._id;
  const companyId = user?.companyId;

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  const [snapshot, setSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const [dbPlans, setDbPlans] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansLoaded, setPlansLoaded] = useState(false);

  const [fleetLatest, setFleetLatest] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);

  const [openNotifyPopup, setopenNotifyPopup] = useState(false);
  const handleOpenNotifyPopup = () => setopenNotifyPopup(true);
  const handleCloseNotifyPopup = () => setopenNotifyPopup(false);
  const handleSubmitNotify = () => navigateTo("/");

  const [openFleetMemPopup, setopenFleetMemPopup] = useState(false);
  const handleOpenFleetMemPopup = () => setopenFleetMemPopup(true);
  const handleCloseFleetMemPopup = () => setopenFleetMemPopup(false);

  const [openTouchSoonPopup, setopenTouchSoonPopup] = useState(false);
  const handleOpenTouchSoonPopup = () => setopenTouchSoonPopup(true);
  const handleCloseTouchSoonPopup = () => setopenTouchSoonPopup(false);
  const handleSubmitTouchSoon = () => setopenTouchSoonPopup(false);

  const [openSubSuccessPopup, setopenSubSuccessPopup] = useState(false);
  const handleCloseSubSuccessPopup = () => setopenSubSuccessPopup(false);

  const [openSubCanceledPopup, setopenSubCanceledPopup] = useState(false);
  const handleCloseSubCanceledPopup = () => setopenSubCanceledPopup(false);

  // ✅ Handle ?success=1 / ?canceled=1 and clean URL
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const success = sp.get("success");
    const canceled = sp.get("canceled");

    if (success === "1") {
      setopenSubSuccessPopup(true);
      sp.delete("success");
      sp.delete("session_id");
      sp.delete("checkout");
      navigateTo(
        { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" },
        { replace: true }
      );
    }

    if (canceled === "1") {
      setopenSubCanceledPopup(true);
      sp.delete("canceled");
      navigateTo(
        { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ✅ Load subscription snapshot
  const refreshSnapshot = async () => {
    if (!userId) return;
    setLoadingSnap(true);
    try {
      const { data } = await backend.get(`/api/stripe/subscriptions/me/${userId}`, { headers });
      setSnapshot(data || {});
    } catch (e) {
      console.error("load sub snapshot error:", e);
      setSnapshot(null);
    } finally {
      setLoadingSnap(false);
    }
  };

  useEffect(() => {
    refreshSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  useEffect(() => {
    if (!openSubSuccessPopup) return;
    refreshSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSubSuccessPopup]);

  // ✅ Load plans
  useEffect(() => {
    async function loadPlans() {
      setLoadingPlans(true);
      try {
        const { data } = await backend.get("/api/plan-config", { headers });
        const items = Array.isArray(data?.items) ? data.items : [];
        const order = ["tier1", "tier2", "tier3", "fleet"];
        items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
        setDbPlans(items);
      } catch (e) {
        console.error("load plans error:", e);
        setDbPlans([]);
      } finally {
        setLoadingPlans(false);
        setPlansLoaded(true);
      }
    }
    loadPlans();
  }, [token]);

  // ✅ Load latest fleet request
  const refreshFleetLatest = async () => {
    if (!userId) return;
    setFleetLoading(true);
    try {
      const { data } = await backend.get(`/api/fleet-requests/me/${userId}/latest`, { headers });
      setFleetLatest(data?.item || null);
    } catch (e) {
      console.error("load fleet latest error:", e);
      setFleetLatest(null);
    } finally {
      setFleetLoading(false);
    }
  };

  useEffect(() => {
    refreshFleetLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  // ✅ Auto-open fleet popup for users with NO history (once per user)
  // useEffect(() => {
  //   if (!userId) return;
  //   if (fleetLoading) return;
  //   if (fleetLatest !== null) return; // if has any latest doc, do nothing
  //   const key = `fc_fleet_prompted_${userId}`;
  //   const already = localStorage.getItem(key) === "1";
  //   if (already) return;

  //   // open once
  //   localStorage.setItem(key, "1");
  //   setBilling("fleet_request");
  //   setopenFleetMemPopup(true);
  // }, [userId, fleetLoading, fleetLatest]);

  // ✅ Plans (unchanged labels/content)
  const plans = useMemo(() => {
    if (!plansLoaded || loadingPlans || dbPlans === null) {
      return [
        { key: "sk_tier1", __skeleton: true, highlight: false },
        { key: "sk_tier2", __skeleton: true, highlight: true },
        { key: "sk_tier3", __skeleton: true, highlight: false },
      ];
    }

    const byKey = new Map((dbPlans || []).map((p) => [p.key, p]));
    const t1 = byKey.get("tier1");
    const t2 = byKey.get("tier2");
    const t3 = byKey.get("tier3");

    const t1m = t1 ? Number(t1?.pricing?.monthly?.amount || 0) : 0;
    const t1b = t1 ? Number(t1?.pricing?.biannually?.amount || 0) : 0;
    const t1a = t1 ? Number(t1?.pricing?.annually?.amount || 0) : 0;

    const t2m = t2 ? Number(t2?.pricing?.monthly?.amount || 20) : 20;
    const t2b = t2 ? Number(t2?.pricing?.biannually?.amount || 108) : 108;
    const t2a = t2 ? Number(t2?.pricing?.annually?.amount || 220) : 220;

    const t3m = t3 ? Number(t3?.pricing?.monthly?.amount || 40) : 40;
    const t3b = t3 ? Number(t3?.pricing?.biannually?.amount || 216) : 216;
    const t3a = t3 ? Number(t3?.pricing?.annually?.amount || 440) : 440;

    return [
      {
        key: "tier1",
        title: "Tier 1",
        subtitle: "Unlimited fill-ups, $9.99 fill-up fee",
        highlight: false,
        pricing: {
          monthly: { amount: `${t1m}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
          biannually: { amount: `${t1b}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
          annually: { amount: `${t1a}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
        },
        features: ["No Vehicle Limit", "Unlimited Fill-Ups", "Fuel at Cost", "No Set Up Fee"],
      },
      {
        key: "tier2",
        title: "Tier 2",
        subtitle: "1 vehicle and 4 fill-ups per month",
        highlight: true,
        pricing: {
          monthly: { amount: `${t2m}`, unit: "/month", billedNote: "Billed Every 6 Months", cta: "Get started - 1 month free" },
          biannually: { amount: `${t2b}`, unit: "/6 months", billedNote: "Billed Bi-Annually", cta: "Get started for 10% off" },
          annually: { amount: `${t2a}`, unit: "/year", billedNote: "Billed Annually", cta: "Get started - 1 month free" },
        },
        features: ["1 Vehicle", "4 Fill-Ups Per Month", "Fuel at Cost", "No Set Up Fee"],
      },
      {
        key: "tier3",
        title: "Tier 3",
        subtitle: "2 vehicles, 4 fill-ups per month per vehicle",
        highlight: false,
        pricing: {
          monthly: { amount: `${t3m}`, unit: "/month", billedNote: "Billed Monthly", cta: "Get started - 2 months free" },
          biannually: { amount: `${t3b}`, unit: "/6 months", billedNote: "Billed Bi-Annually", cta: "Get started for 10% off" },
          annually: { amount: `${t3a}`, unit: "/year", billedNote: "Billed Annually", cta: "Get started - one month free" },
        },
        features: ["2 Vehicles", "4 Fill-Ups Per Month Per Vehicle", "Fuel at Cost", "No Set Up Fee"],
      },
    ];
  }, [dbPlans, loadingPlans, plansLoaded]);

  const cycleMap = { monthly: "monthly", biannually: "biannually", annually: "annually" };
  const safeBilling = billing === "fleet_request" ? "monthly" : billing;

  const getCurrentPricing = (plan) => plan?.pricing?.[safeBilling];
  const priceLabel = (plan) => {
    const p = getCurrentPricing(plan);
    return (
      <>
        <span className={Classes.price}>${p?.amount ?? "—"}</span>
        <span className={Classes.per}>{p?.unit ?? ""}</span>
      </>
    );
  };

  const overview = [
    { label: "Vehicles", tier1: "No Vehicle Limit", tier2: "1", tier3: "2" },
    { label: "Fill Ups Per Month", tier1: "Unlimited", tier2: "4", tier3: "4 Per Vehicle" },
    { label: "Fuel at Cost", tier1: "Yes", tier2: "Yes", tier3: "Yes" },
    { label: "Set Up Fee (One Time)", tier1: "$0", tier2: "$0", tier3: "$0" },
  ];

  // ✅ tiers checkout
  async function startSubscription(planKey, vehiclesCount = 0) {
    if (!userId) return;
    const billingCycle = cycleMap[safeBilling] || "monthly";

    const payload = {
      userId,
      companyId,
      planKey,
      billingCycle,
      vehiclesCount,
      success_url: `${window.location.origin}/settings/membership?success=1`,
      cancel_url: `${window.location.origin}/settings/membership?canceled=1`,
    };

    const { data } = await backend.post("/api/stripe/subscriptions/checkout", payload, { headers });
    if (data?.url) window.location.href = data.url;
  }

  const membershipClick = (planKey) => startSubscription(planKey);

  // ✅ submit fleet request
  async function submitFleetRequest(vehicles, notes) {
    if (!userId) return;
    const v = Math.max(1, Number(vehicles || 1));

    await backend.post(
      "/api/fleet-requests/submit",
      { userId, companyId, vehiclesCount: v, notes: notes || "" },
      { headers }
    );

    await refreshFleetLatest();
    handleOpenTouchSoonPopup();
  }

  // ✅ fleet checkout from approved request
  const [fleetCheckoutLoading, setFleetCheckoutLoading] = useState(false);

  async function checkoutApprovedFleet() {
    if (!userId || !fleetLatest?._id) return;
    setFleetCheckoutLoading(true);
    try {
      const payload = {
        userId,
        companyId,
        fleetRequestId: fleetLatest._id,
        success_url: `${window.location.origin}/settings/membership?success=1`,
        cancel_url: `${window.location.origin}/settings/membership?canceled=1`,
      };

      const { data } = await backend.post("/api/stripe/subscriptions/fleet/checkout", payload, { headers });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      console.error("fleet checkout error:", e);
      setopenSubCanceledPopup(true);
    } finally {
      setFleetCheckoutLoading(false);
    }
  }

  // cancel at period end
  async function cancelAtPeriodEnd() {
    if (!userId) return;
    setCanceling(true);
    try {
      await backend.post("/api/stripe/subscriptions/cancel", { userId }, { headers });
      await refreshSnapshot();
    } catch (e) {
      console.error("cancel sub error:", e);
    } finally {
      setCanceling(false);
    }
  }

  // ---------- derived state ----------
  const active = snapshot?.activeSubscription || {};
  const planKey = active?.plan?.key || snapshot?.legacyLabel || "free";
  const planCycle = active?.plan?.billingCycle || active?.billingCycle || "";
  const periodStart = active?.current_period_start;
  const periodEnd = active?.current_period_end;
  const cancelAtEnd = !!active?.cancel_at_period_end;
  const status = (active?.status || "none").toLowerCase();

  const nowTs = Date.now();
  const endTs = periodEnd ? new Date(periodEnd).getTime() : null;
  const isEnded = !!endTs && endTs < nowTs;
  const isCancelled = status === "canceled" || status === "cancelled";
  const hasActiveSub = status !== "none" && !isCancelled && !isEnded;

  const isPlanSubscribed = (pKey) => hasActiveSub && pKey === planKey;

  const canCancel = hasActiveSub && !cancelAtEnd && !canceling;
  const cancelLabel = cancelAtEnd ? "Already set to cancel" : canceling ? "Cancelling..." : "Cancel membership";

  // Fleet state
  const fleetStatus = (fleetLatest?.status || "").toLowerCase();
  const fleetPending = fleetStatus === "pending";
  const fleetApproved = fleetStatus === "approved";

  const approvedVehicles = fleetLatest?.approvedVehiclesCount || fleetLatest?.vehiclesCount || 0;
  const approvedPrice = fleetLatest?.approvedPricePerVehicle;

  // Toggle bg for 4 buttons
  const toggleBgLeft =
    billing === "monthly"
      ? "4px"
      : billing === "biannually"
      ? "calc(25% + 4px)"
      : billing === "annually"
      ? "calc(50% + 4px)"
      : "calc(75% + 4px)";

  return (
    <>
      <h3 className="mb-7">Membership</h3>

      {/* Current subscription summary */}
      {hasActiveSub && (
        <motion.div className={Classes.currentBox} animate="show" variants={fadeUp}>
          <div className={Classes.currentRow}>
            <div>
              <div className={Classes.currentLabel}>Current Membership</div>

              <div className={Classes.currentVal}>
                {loadingSnap ? "Loading..." : hasActiveSub ? labelFor(planKey) : "None"}
                {hasActiveSub && status !== "none" ? <span className={Classes.badge}>{status}</span> : null}
                {hasActiveSub && planCycle ? (
                  <span className={Classes.badge} style={{ marginLeft: 8 }}>
                    {cycleLabelFor(planCycle)}
                  </span>
                ) : null}
              </div>

              <button
                disabled={!canCancel}
                onClick={canCancel ? cancelAtPeriodEnd : undefined}
                className={`${Classes.cta} ${Classes.ghostBtn} ${!canCancel ? Classes.disabledBtn : ""}`}
                style={{ minWidth: 180 }}
              >
                {cancelLabel}
              </button>
            </div>

            <div className={Classes.periodWrap}>
              <div className={Classes.period}>
                <span>Period Start</span>
                <strong>{hasActiveSub ? fmt(periodStart) : "—"}</strong>
              </div>
              <div className={Classes.period}>
                <span>Period End</span>
                <strong>{hasActiveSub ? fmt(periodEnd) : "—"}</strong>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <h5 className={Classes.text}>Choose the billing cycle that works best for you.</h5>

      <motion.section className={Classes.page} whileInView="show" viewport={{ once: true, amount: 0.2 }}>
        {/* Billing toggle */}
        <motion.div className={Classes.toggleWrap} variants={fadeUp}>
          <div className={Classes.toggle}>
            <motion.span
              className={Classes.toggleBg}
              animate={{ left: toggleBgLeft }}
              transition={{ type: "spring", stiffness: 520, damping: 32 }}
            />

            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "monthly" ? Classes.active : ""}`}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>

            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "biannually" ? Classes.active : ""}`}
              onClick={() => setBilling("biannually")}
            >
              Bi-Annually
            </button>

            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "annually" ? Classes.active : ""}`}
              onClick={() => setBilling("annually")}
            >
              Annually
            </button>

            {/* Fleet request toggle */}
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "fleet_request" ? Classes.active : ""}`}
              onClick={() => {
                setBilling("fleet_request");
                handleOpenFleetMemPopup();
              }}
              disabled={fleetPending}
              aria-disabled={fleetPending}
              title={fleetPending ? "Fleet request is pending" : "Request Fleet"}
            >
              {fleetPending ? "Fleet Pending" : "Fleet Request"}
            </button>
          </div>

          <div className={Classes.toggleNote}>Get 1 month free by billing annually</div>

          <motion.button className={Classes.footerLink} variants={fadeUp} onClick={handleOpenNotifyPopup}>
            Proceed without a membership
          </motion.button>

          {/* ✅ If fleet approved, show checkout + request new */}
          {fleetApproved && (
            <div className={Classes.currentBox} style={{ width: "100%", maxWidth: 1100, padding: "14px 0" }}>
              <div className={Classes.currentRow}>
                <div>
                  <div className={Classes.currentLabel}>Fleet Request Approved</div>
                  <div className={Classes.currentVal}>
                    Fleet approved for <span className={Classes.badge}>{approvedVehicles} vehicles</span>
                    {approvedPrice !== null && approvedPrice !== undefined ? (
                      <span className={Classes.badge} style={{ marginLeft: 8 }}>
                        ${approvedPrice}/vehicle
                      </span>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                    <button
                      type="button"
                      className={`${Classes.cta} ${fleetCheckoutLoading ? Classes.disabledBtn : ""}`}
                      onClick={fleetCheckoutLoading ? undefined : checkoutApprovedFleet}
                      disabled={fleetCheckoutLoading}
                      style={{ width: 220 }}
                    >
                      {fleetCheckoutLoading ? "Redirecting..." : "Checkout Fleet"}
                    </button>

                    <button
                      type="button"
                      className={`${Classes.cta} ${Classes.ghostBtn}`}
                      onClick={() => {
                        setBilling("fleet_request");
                        setopenFleetMemPopup(true);
                      }}
                      style={{ width: 220 }}
                    >
                      Request New Fleet
                    </button>
                  </div>
                </div>

                <div className={Classes.periodWrap}>
                  <div className={Classes.period}>
                    <span>Approved At</span>
                    <strong>{fleetLatest?.approvedAt ? fmt(fleetLatest.approvedAt) : "—"}</strong>
                  </div>
                  <div className={Classes.period}>
                    <span>Status</span>
                    <strong>{fleetLatest?.status || "—"}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* small helper status */}
          {billing === "fleet_request" && (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
              {fleetLoading ? "Loading fleet status…" : fleetLatest ? `Latest Fleet Request: ${fleetLatest.status}` : "No fleet request found."}
            </div>
          )}
        </motion.div>

        {/* Plan cards */}
        <motion.div className={Classes.grid} variants={stagger}>
          <div className={Classes.extraCard} />

          {plans.map((p) => {
            if (p.__skeleton) {
              return (
                <motion.div
                  key={p.key}
                  className={`${Classes.card} ${p.highlight ? Classes.cardHighlight : ""}`}
                  variants={cardIn}
                  whileHover={{ y: -6 }}
                  style={{ opacity: 0.55, pointerEvents: "none" }}
                >
                  <div className={Classes.cardHead}>
                    <div className={Classes.cardTitle}>Tier</div>
                    <div className={Classes.cardSubtitle}>Loading...</div>
                  </div>
                  <div className={Classes.priceRow}>
                    <span className={Classes.price}>$—</span>
                    <span className={Classes.per}></span>
                  </div>
                  <div className={Classes.billed}>Loading...</div>
                  <button type="button" disabled className={`${Classes.cta} ${Classes.disabledBtn}`}>
                    Loading...
                  </button>
                </motion.div>
              );
            }

            const subscribed = isPlanSubscribed(p.key);
            const currentPricing = getCurrentPricing(p);

            return (
              <motion.div
                key={p.key}
                className={`${Classes.card} ${p.highlight ? Classes.cardHighlight : ""}`}
                variants={cardIn}
                whileHover={{ y: -6 }}
              >
                <div className={Classes.cardHead}>
                  <div className={Classes.cardTitle}>{p.title}</div>
                  <div className={Classes.cardSubtitle}>{p.subtitle}</div>
                </div>

                <div className={Classes.priceRow}>{priceLabel(p)}</div>
                <div className={Classes.billed}>{currentPricing?.billedNote}</div>

                <button
                  type="button"
                  disabled={subscribed || billing === "fleet_request"}
                  aria-disabled={subscribed || billing === "fleet_request"}
                  onClick={subscribed || billing === "fleet_request" ? undefined : () => membershipClick(p.key)}
                  className={`${Classes.cta} ${subscribed || billing === "fleet_request" ? Classes.disabledBtn : ""}`}
                >
                  {subscribed ? "Subscribed" : currentPricing?.cta}
                </button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Overview */}
        <motion.div className={Classes.overview} variants={fadeUp}>
          <div className={Classes.overviewLabel}>Overview</div>
          <div className={Classes.table}>
            <div className={`${Classes.tr} ${Classes.trHead}`}>
              <div className={Classes.td} />
              <div className={Classes.td}>Tier 1</div>
              <div className={Classes.td}>Tier 2</div>
              <div className={Classes.td}>Tier 3</div>
            </div>
            {overview.map((r, i) => (
              <div className={Classes.tr} key={i}>
                <div className={`${Classes.td} ${Classes.rowLabel}`}>{r.label}</div>
                <div className={Classes.td} data-th="Tier 1">{r.tier1}</div>
                <div className={Classes.td} data-th="Tier 2">{r.tier2}</div>
                <div className={Classes.td} data-th="Tier 3">{r.tier3}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* cancel */}
        {hasActiveSub && (
          <motion.button
            onClick={canCancel ? cancelAtPeriodEnd : undefined}
            disabled={!canCancel}
            aria-disabled={!canCancel}
            className={`${Classes.footerLink} ${Classes.cancelBtn} ${!canCancel ? Classes.disabledBtn : ""}`}
            style={{ color: "#FF0000", marginBottom: "3rem" }}
            variants={fadeUp}
          >
            {cancelLabel}
          </motion.button>
        )}
      </motion.section>

      {/* Popups */}
      <AnimatePresence exit="wait">
        {openNotifyPopup && (
          <NotifyPopup
            closePopup={handleCloseNotifyPopup}
            submitPopup={handleSubmitNotify}
            title="Are you sure you want to proceed without a membership?"
            description={"Without a membership, you are charged a $9.99 fee per fill up."}
            buttonText="Proceed"
            icon={<ExclamationIcon fill={"white"} height="50px" />}
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openSubSuccessPopup && (
          <NotifyPopup
            closePopup={handleCloseSubSuccessPopup}
            submitPopup={() => setopenSubSuccessPopup(false)}
            title="Subscription Activated"
            description={"Your subscription was successful and your membership is now active."}
            buttonText="OK"
            icon={<EmailSentIcon fill={"white"} />}
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openSubCanceledPopup && (
          <NotifyPopup
            closePopup={handleCloseSubCanceledPopup}
            submitPopup={() => setopenSubCanceledPopup(false)}
            title="Checkout Canceled"
            description={"Your checkout was canceled. You can try again anytime."}
            buttonText="OK"
            icon={<ExclamationIcon fill={"white"} height="50px" />}
          />
        )}
      </AnimatePresence>

      {/* Fleet popup submits fleet request */}
      <AnimatePresence exit="wait">
        {openFleetMemPopup && (
          <FleetMembPopup
            closePopup={() => {
              handleCloseFleetMemPopup();
              if (billing === "fleet_request") setBilling("monthly");
            }}
            submitPopup={async (vehicles, notes) => {
              await submitFleetRequest(vehicles, notes);
              handleCloseFleetMemPopup();
              if (billing === "fleet_request") setBilling("monthly");
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openTouchSoonPopup && (
          <NotifyPopup
            closePopup={handleCloseTouchSoonPopup}
            submitPopup={handleSubmitTouchSoon}
            title="We’ll Be in Touch Soon"
            description={"Your inquiry for fleet membership has been sent successfully. A member of our team will be in touch shortly."}
            buttonText="Return"
            icon={<EmailSentIcon fill={"white"} />}
          />
        )}
      </AnimatePresence>
    </>
  );
}
